import type { GridProps, IconButtonProps } from '@hover/blueprint';
import {
  Box,
  Grid,
  Heading,
  IconButton,
  forwardRef,
  motion,
} from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import { iCheck, iX } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { Web } from 'src/shared/constants/FeatureFlag';
import {
  TranslationNamespace,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { isEnabled } from 'src/shared/telemetry/featureFlag';
import { fadePresence } from 'src/shared/utils/motion';

import type { CustomizationCategory } from '../../constants/customizationCategories';
import { nudge } from '../../helpers';
import { CobrandLogo } from '../CobrandLogo/CobrandLogo';

import { getCategoryHeading } from './menu';
import { useCategoryCobranding } from './partners';
import { useCustomization } from './useCustomization';

interface CustomizeMobileHeadingProps extends GridProps {
  category: CustomizationCategory | null;
  hideApplyCustomization?: boolean;
  onBack?: () => void;
  backButtonIcon?: IconProp;
}

const buttonProps = {
  shape: 'circle',
  size: 'small',
  iconSize: 'small',
  placeSelf: 'center',
  sx: {
    svg: { strokeLinecap: 'square', strokeWidth: '2px' },
  },
} satisfies Partial<IconButtonProps>;

const MotionIconButton = motion(IconButton);

export const CustomizeMobileHeading = forwardRef<
  CustomizeMobileHeadingProps,
  'div'
>(
  (
    {
      category,
      hideApplyCustomization = false,
      onBack,
      backButtonIcon = iX,
      ...props
    },
    ref,
  ) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

    const isNewProjectUiEnabled = isEnabled(useTreatment(Web.NEW_PROJECT_UI));

    const cobranding = useCategoryCobranding();

    const { history, isCustomizing, onApply, onCancel } = useCustomization();
    const showCheckmarkIcon =
      history.hasChanges &&
      category &&
      !isCustomizing &&
      !hideApplyCustomization;

    return (
      <Grid
        gridTemplateColumns="auto 1fr auto"
        position="relative"
        {...props}
        ref={ref}
      >
        <AnimatePresence mode="popLayout">
          {category && (
            <MotionIconButton
              gridColumn={1}
              gridRow={1}
              icon={backButtonIcon}
              key="close"
              label={t('customize.close')}
              onClick={onBack ?? onCancel}
              {...fadePresence}
              {...buttonProps}
              {...nudge(100, 'left')}
            />
          )}
          <Box
            alignItems="center"
            display="flex"
            flexDirection="row"
            gap={200}
            gridColumn="1/4"
            gridRow={1}
            justifyContent="center"
            width="100%"
          >
            <Heading
              as="h1"
              color="neutral.0"
              noMargin
              placeSelf="center"
              size={400}
              textAlign="center"
              textStyle="body500"
            >
              {getCategoryHeading(t, category)}
            </Heading>
            {isNewProjectUiEnabled && cobranding && (
              <CobrandLogo colorMode="dark" hideHoverMark />
            )}
          </Box>
          {showCheckmarkIcon && (
            <MotionIconButton
              gridColumn={3}
              gridRow={1}
              icon={iCheck}
              key="apply"
              label={t('customize.apply')}
              onClick={onApply}
              {...fadePresence}
              {...buttonProps}
              {...nudge(100, 'right')}
            />
          )}
        </AnimatePresence>
      </Grid>
    );
  },
);
